import { clamp } from '@/core/rng';

/**
 * How wrong a witness is about a sign (v0.3.2 §Omen).
 *
 * Omen is the informational verb: it changes attention, never conditions.
 * The single thing standing between that idea and "cast Omen → mortals
 * pathfind here" is this file. A sign is not a map pin; what a witness gets
 * is a direction and a feeling, and both of these numbers say how bad the
 * reading is.
 *
 * It lives on its own because the simulation and the mortal layer both need
 * it and neither may import the other.
 */

/**
 * The radius of doubt, in metres.
 *
 * Two things blur a sign. How broadly the god cast it — a whisper over one
 * tree is unmistakable, a portent across the watershed could have meant
 * anywhere inside it — and how far away the witness was standing, because
 * the far side of a valley is a guess about a guess. This is what makes
 * scale a real choice rather than a power level: a small Omen is a precise
 * instruction to the few people under it, and a vast one is a rumour that
 * a whole region will argue about for generations.
 */
export const omenUncertainty = (radius: number, distance: number): number =>
  radius * 0.8 + distance * 0.25;

/**
 * 0..1 — how sharply the reading came through. Falls away fast: by a few
 * hundred metres of doubt a sign is already more mood than instruction.
 */
export const omenSpecificity = (uncertainty: number): number =>
  clamp(200 / (200 + Math.max(0, uncertainty)), 0, 1);
